import type { TimedMusicXmlNote } from "./songEngine";

export interface PlaybackSegment {
  /** Primera nota del evento (índice en el array de notas). */
  noteIndex: number;
  startTime: number;
  endTime: number;
  startMs: number;
  endMs: number;
}

export interface SongPlaybackTimeline {
  segments: PlaybackSegment[];
  totalMs: number;
  /** startTime → índice de la primera nota en ese instante */
  timeToNoteIndex: Map<number, number>;
}

/** Construye una línea de tiempo por instantes únicos (acordes = un solo evento). */
export function buildSongPlaybackTimeline(
  notes: TimedMusicXmlNote[],
  divisions: number,
  bpm: number
): SongPlaybackTimeline {
  if (notes.length === 0) {
    return { segments: [], totalMs: 0, timeToNoteIndex: new Map() };
  }

  const beatMs = (60 / bpm) * 1000;
  let uniqueStarts = [...new Set(notes.map((n) => n.startTime))].sort((a, b) => a - b);
  if (uniqueStarts.length > 0 && uniqueStarts[0] > 0) {
    uniqueStarts = [0, ...uniqueStarts];
  }
  const timeToNoteIndex = new Map<number, number>();

  for (const t of uniqueStarts) {
    let idx = notes.findIndex((n) => n.startTime === t && !n.isRest);
    if (idx < 0) idx = notes.findIndex((n) => n.startTime === t);
    if (idx < 0 && t === 0) idx = notes.findIndex((n) => !n.isRest);
    timeToNoteIndex.set(t, idx >= 0 ? idx : 0);
  }

  const segments: PlaybackSegment[] = [];
  let ms = 0;

  for (let i = 0; i < uniqueStarts.length; i++) {
    const startTime = uniqueStarts[i];
    const nextStart =
      i + 1 < uniqueStarts.length
        ? uniqueStarts[i + 1]
        : startTime + maxDurationAtTime(notes, startTime, divisions);

    const durationDiv = Math.max(1, nextStart - startTime);
    const durationMs = (durationDiv / divisions) * beatMs;

    segments.push({
      noteIndex: timeToNoteIndex.get(startTime) ?? 0,
      startTime,
      endTime: nextStart,
      startMs: ms,
      endMs: ms + durationMs,
    });
    ms += durationMs;
  }

  return { segments, totalMs: ms, timeToNoteIndex };
}

/** Ms en que empieza el primer ataque con nota real (no silencio). 0 si empieza en 0. */
export function getFirstNonRestAttackStartMs(
  notes: TimedMusicXmlNote[],
  timeline: SongPlaybackTimeline
): number {
  const starts = [
    ...new Set(notes.filter((n) => !n.isRest).map((n) => n.startTime)),
  ].sort((a, b) => a - b);
  const t = starts[0];
  if (t === undefined) return 0;
  const seg = timeline.segments.find((s) => s.startTime === t);
  return seg?.startMs ?? 0;
}

/**
 * Pulso para la entrada al compás: corchea en compases con denominador 8 (6/8, 9/8…),
 * negra en denominador 4 o 2 (4/4, 3/4, 2/4…).
 */
export function getCountInPulseMs(bpm: number, beatType: number): number {
  const quarterMs = (60 / bpm) * 1000;
  if (beatType === 8) return quarterMs / 2;
  return quarterMs;
}

function maxDurationAtTime(
  notes: TimedMusicXmlNote[],
  startTime: number,
  divisions: number
): number {
  const atTime = notes.filter((n) => n.startTime === startTime);
  if (atTime.length === 0) return divisions;
  return Math.max(...atTime.map((n) => n.duration), divisions / 4);
}

export function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

/**
 * Tiempo musical (divisiones) para alinear pentagrama y mástil al primer ataque real.
 * En anacrusa evita anclar en el inicio del compás (0) cuando la primera figura suena más tarde.
 */
/** Ms en la línea de tiempo correspondientes a un instante musical (divisiones). */
export function msAtDivision(timeline: SongPlaybackTimeline, divTime: number): number {
  const { segments } = timeline;
  if (segments.length === 0) return 0;
  if (divTime <= segments[0].startTime + 1e-9) return segments[0].startMs;
  for (const seg of segments) {
    if (divTime < seg.endTime - 1e-9) {
      if (divTime <= seg.startTime + 1e-9) return seg.startMs;
      const span = seg.endTime - seg.startTime;
      const u = (divTime - seg.startTime) / Math.max(span, 1e-9);
      return seg.startMs + u * (seg.endMs - seg.startMs);
    }
  }
  return segments[segments.length - 1].endMs;
}

/**
 * Duración de la cuenta de entrada (ms): en anacrusa, hasta el **fin del compás de entrada**
 * (barra del compás completa) para que los pulsos recorran todo el hueco rítmico antes del golpe fuerte.
 */
export function getCountInEndMs(
  timeline: SongPlaybackTimeline,
  measures: Array<{ startTime: number; length: number; isPickup: boolean }>,
  firstAttackStartMs: number
): number {
  const m0 = measures[0];
  if (!m0?.isPickup || measures.length === 0) return firstAttackStartMs;
  const barEndDiv = m0.startTime + m0.length;
  const atBar = msAtDivision(timeline, barEndDiv);
  const capped = Math.min(timeline.totalMs, atBar);
  return Math.max(firstAttackStartMs, capped);
}

/** X horizontal en el SVG del pentagrama para un instante musical (interpolación entre ataques medidos). */
export function staffPixelXAtTime(
  samples: Array<{ startTime: number; x: number }>,
  divisionTime: number
): number {
  if (samples.length === 0) return 0;
  const t = divisionTime;
  const first = samples[0];
  const last = samples[samples.length - 1];
  /**
   * Nunca extrapolar *hacia la izquierda* del primer muestreo: el espacio previo (clave, cifra)
   * no sigue el mismo ritmo que los ataques; el timeline a veces usa t=0 sintético con primera
   * muestra en el primer ataque real → la extrapolación colocaba la aguja sobre la clave.
   */
  if (t < first.startTime - 1e-9) {
    return first.x;
  }
  if (t >= last.startTime) {
    if (samples.length >= 2) {
      const prev = samples[samples.length - 2];
      const rate =
        (last.x - prev.x) / Math.max(last.startTime - prev.startTime, 1e-9);
      return last.x + (t - last.startTime) * rate;
    }
    return last.x;
  }
  let i = 1;
  while (i < samples.length && samples[i].startTime < t) i++;
  const a = samples[i - 1];
  const b = samples[i];
  const span = b.startTime - a.startTime;
  const u = span > 1e-9 ? (t - a.startTime) / span : 0;
  return a.x + (b.x - a.x) * u;
}

/** Posición X del playhead interpolada entre eventos (coordenadas de contenido, sin márgenes). */
export function interpolatePlayheadContentX(
  timeline: SongPlaybackTimeline,
  playbackMs: number,
  timeToX: (startTime: number) => number
): number {
  const { segments } = timeline;
  if (segments.length === 0) return 0;

  if (playbackMs <= 0) return timeToX(segments[0].startTime);

  if (playbackMs >= timeline.totalMs) {
    const last = segments[segments.length - 1];
    return timeToX(last.endTime);
  }

  for (const seg of segments) {
    if (playbackMs >= seg.startMs && playbackMs < seg.endMs) {
      const t = clamp01((playbackMs - seg.startMs) / (seg.endMs - seg.startMs));
      const x0 = timeToX(seg.startTime);
      const x1 = timeToX(seg.endTime);
      return x0 + (x1 - x0) * t;
    }
  }

  const last = segments[segments.length - 1];
  return timeToX(last.endTime);
}

/** Índice de nota activa y progreso dentro del segmento actual. */
export function getPlaybackNoteState(
  timeline: SongPlaybackTimeline,
  playbackMs: number
): { noteIndex: number; eventIndex: number; segmentProgress: number } {
  if (timeline.segments.length === 0) {
    return { noteIndex: 0, eventIndex: 0, segmentProgress: 0 };
  }

  if (playbackMs >= timeline.totalMs) {
    const lastIdx = timeline.segments.length - 1;
    return {
      noteIndex: timeline.segments[lastIdx].noteIndex,
      eventIndex: lastIdx,
      segmentProgress: 1,
    };
  }

  for (let i = 0; i < timeline.segments.length; i++) {
    const seg = timeline.segments[i];
    if (playbackMs >= seg.startMs && playbackMs < seg.endMs) {
      return {
        noteIndex: seg.noteIndex,
        eventIndex: i,
        segmentProgress: clamp01((playbackMs - seg.startMs) / (seg.endMs - seg.startMs)),
      };
    }
  }

  return {
    noteIndex: timeline.segments[0].noteIndex,
    eventIndex: 0,
    segmentProgress: 0,
  };
}

/**
 * Ancla de contenido para alinear scroll y playhead.
 * Prefiere la **voz 1** (melodía habitual en MusicXML); si no hay notas con altura en esa voz,
 * usa el primer ataque no-silencio más temprano entre todas las voces.
 * Sin notas con altura: `fallbackStartTime` (inicio del primer compás).
 */
export function getPlaybackContentAnchorStartTime(
  notes: TimedMusicXmlNote[],
  fallbackStartTime: number
): number {
  if (!notes || notes.length === 0) return fallbackStartTime;
  const v1 = notes.filter((n) => !n.isRest && (n.voice ?? 1) === 1);
  if (v1.length > 0) {
    return Math.min(...v1.map((n) => n.startTime));
  }
  const pitched = notes.filter((n) => !n.isRest);
  if (pitched.length === 0) return fallbackStartTime;
  return Math.min(...pitched.map((n) => n.startTime));
}

/**
 * El timeline puede insertar t=0 sin evento cuando el 1.er ataque es >0 (huecos “sintéticos”).
 * Ahí hay que alinear X con el ancla para no dibujar la aguja sobre la clave.
 * Si en **voz 1** hay silencio o nota en ese instante, **no** reescribir: hay que usar la X
 * real del silencio durante su segmento (si no, la aguja salta y “se come” el compás teórico).
 */
export function snapLeadingRestStaffTime(
  divisionTime: number,
  notes: TimedMusicXmlNote[],
  anchorStartTime: number
): number {
  if (anchorStartTime <= 1e-9) return divisionTime;
  const v1EventHere = notes.some(
    (n) => Math.abs(n.startTime - divisionTime) < 1e-9 && (n.voice ?? 1) === 1
  );
  if (v1EventHere) return divisionTime;
  if (divisionTime < anchorStartTime - 1e-9) return anchorStartTime;
  return divisionTime;
}

/**
 * Si el timeline pregunta por un instante **antes** del primer `startTime` medido en el pentagrama,
 * pero el ancla de contenido cae en ese primer muestreo (p. ej. geometría [{t:4,x:…}] y ancla 4),
 * usa el ancla para la X del playhead.
 */
export function alignPlayheadDivisionToStaffGeometryStart(
  divisionTime: number,
  anchorStartTime: number,
  geometry: Array<{ startTime: number; x: number }>
): number {
  if (geometry.length === 0) return divisionTime;
  const firstT = geometry[0].startTime;
  if (divisionTime + 1e-9 < firstT && anchorStartTime + 1e-9 >= firstT) {
    return anchorStartTime;
  }
  return divisionTime;
}

/** startTime activo según ms (para resaltar acordes completos). */
export function getActiveStartTime(
  timeline: SongPlaybackTimeline,
  playbackMs: number
): number {
  if (timeline.segments.length === 0) return -1;
  const state = getPlaybackNoteState(timeline, playbackMs);
  return timeline.segments[state.eventIndex]?.startTime ?? -1;
}
